// ============================================
// COMPONENT - Modal de gestión rápida de caja, para usar sobre otras páginas (Ventas)
// sin navegar fuera de ellas.
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@almacenes/ui';
import { useMiTurno, useCerrarTurno, useUIStore, useSession, type TurnoCaja } from '@almacenes/core';
import { MdClose, MdLock } from 'react-icons/md';
import { FormularioApertura } from './FormularioApertura';
import { CerrarTurnoModal } from './CerrarTurnoModal';
import { KpiTile } from './KpiTile';
import { ImprimirComprobanteTurnoModal } from './ImprimirComprobanteTurnoModal';
import { formatCurrency, formatearFechaHora } from '../utils/formato';

interface GestionCajaModalProps {
  onClose: () => void;
}

const ModalShell = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-2xl font-bold" style={{ color: '#11194F' }}>{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <MdClose className="text-2xl" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

export const GestionCajaModal = ({ onClose }: GestionCajaModalProps) => {
  const navigate = useNavigate();
  const addToast = useUIStore((state) => state.addToast);
  const { data: miTurno, isLoading } = useMiTurno();
  const turno = miTurno?.turno ?? null;
  const turnosOtrasSucursales = miTurno?.turnosOtrasSucursales ?? [];
  const { data: session } = useSession();
  const cerrarTurno = useCerrarTurno();
  const [vistaCierre, setVistaCierre] = useState(false);
  const [turnoCerrado, setTurnoCerrado] = useState<TurnoCaja | null>(null);

  const handleCerrar = (efectivoContado: number | undefined) => {
    if (!turno) return;
    cerrarTurno.mutate(
      { turnoId: turno.id, dto: { efectivoContado } },
      {
        onSuccess: (turnoResultante) => {
          addToast('success', 'Caja cerrada exitosamente');
          setTurnoCerrado(turnoResultante);
        },
        onError: (err: any) => {
          if (err?.statusCode === 404) {
            addToast('error', 'Los datos de tu turno estaban desactualizados. Se actualizaron, revisa e intenta de nuevo.');
            onClose();
            return;
          }
          addToast('error', err?.message || 'Error al cerrar la caja');
        },
      }
    );
  };

  const handleVerCajaCompleta = () => {
    onClose();
    navigate('/caja/mi-caja');
  };

  if (turnoCerrado) {
    return <ImprimirComprobanteTurnoModal turno={turnoCerrado} onClose={onClose} />;
  }

  if (isLoading) {
    return (
      <ModalShell title="Mi caja" onClose={onClose}>
        <div className="flex justify-center py-10"><Spinner size="lg" /></div>
      </ModalShell>
    );
  }

  if (!turno) {
    return (
      <ModalShell title="Abrir mi caja" onClose={onClose}>
        {turnosOtrasSucursales.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs font-semibold text-amber-800 mb-1">
              {turnosOtrasSucursales.length === 1
                ? 'Tenés un turno abierto en otra sucursal:'
                : 'Tenés turnos abiertos en otras sucursales:'}
            </p>
            <ul className="text-xs text-amber-700 space-y-0.5">
              {turnosOtrasSucursales.map((t) => (
                <li key={t.turnoId}>📍 {t.direccionAlmacen ?? `Sucursal #${t.almacenId}`}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-sm text-gray-600 mb-4">
          Ábrela para llevar el registro de tus ventas del día.
        </p>
        <FormularioApertura chrome="none" onSuccess={onClose} />
      </ModalShell>
    );
  }

  if (vistaCierre) {
    return (
      <CerrarTurnoModal
        turno={turno}
        onClose={onClose}
        onCancelar={() => setVistaCierre(false)}
        onConfirmar={handleCerrar}
        isLoading={cerrarTurno.isPending}
      />
    );
  }

  return (
    <ModalShell title="Mi caja" onClose={onClose}>
      {/* Igual que en MiCajaPage: en negocios con varias sucursales bajo el
          mismo RUT el nombre se repite idéntico en todas — la dirección es lo
          que permite confirmar que se está gestionando la sucursal correcta. */}
      {session?.almacen?.direccion && (
        <p className="text-xs text-gray-400 mb-1">📍 {session.almacen.direccion}</p>
      )}
      <p className="text-sm text-gray-500 mb-4">
        Abierto el {formatearFechaHora(turno.fechaHoraApertura)} · Monto inicial {formatCurrency(turno.montoInicial)}
      </p>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <KpiTile label="Total ventas" value={formatCurrency(turno.totalVentas)} variant="compact" />
        <KpiTile label="Ingresos (todos los medios)" value={formatCurrency(turno.totalIngresos)} variant="compact" />
        {turno.totalIngresosEfectivo != null && (
          <KpiTile label="Ingresos en efectivo" value={formatCurrency(turno.totalIngresosEfectivo)} variant="compact" />
        )}
        <KpiTile label="Efectivo esperado" value={formatCurrency(turno.efectivoEsperado)} variant="compact" highlight />
      </div>

      <div className="flex flex-col gap-3">
        <Button variant="danger" onClick={() => setVistaCierre(true)}>
          <MdLock className="inline-block mr-2 text-xl" />
          Cerrar mi caja
        </Button>
        <button
          type="button"
          onClick={handleVerCajaCompleta}
          className="text-sm font-medium underline text-center"
          style={{ color: '#43BFFF' }}
        >
          Ver mi caja completa
        </button>
      </div>
    </ModalShell>
  );
};
